import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { RiSettings4Line } from 'react-icons/ri';

import WidgetManager from './WidgetManager';
import widgetRegistry, { getDefaultPreferences } from './widgetRegistry';
import useSidebarPreferences from '../../hooks/useSidebarPreferences';
import { useAuth } from '../../contexts/auth-context';

const RightSidebar = ({ className = '', excludeWidgets = [] }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const defaults = useMemo(() => getDefaultPreferences(), []);
  const { preferences, toggleWidget, moveWidget, resetPreferences } =
    useSidebarPreferences(defaults);
  const [isManagerOpen, setIsManagerOpen] = useState(false);

  const excluded = useMemo(
    () => new Set(excludeWidgets || []),
    [excludeWidgets]
  );

  const managerPreferences = preferences.filter(
    (pref) =>
      !excluded.has(pref.id) &&
      (!widgetRegistry[pref.id]?.adminOnly || user?.isAdmin)
  );

  const visibleWidgets = preferences
    .filter(
      (pref) =>
        pref.enabled &&
        widgetRegistry[pref.id] &&
        !excluded.has(pref.id) &&
        (!widgetRegistry[pref.id]?.adminOnly || user?.isAdmin)
    )
    .map((pref) => ({ pref, widget: widgetRegistry[pref.id] }));

  return (
    <aside
      className={`flex flex-col gap-3 py-4 pr-2 ${className || ''}`}
      aria-label={t('rightSidebar.ariaLabel')}
    >
      <div className="relative flex items-center justify-between px-2">
        <span className="text-xs uppercase tracking-wide text-on-surface/50">
          {t('rightSidebar.title')}
        </span>
        <button
          type="button"
          onClick={() => setIsManagerOpen((value) => !value)}
          className="p-1 rounded-full text-on-surface/70 hover:bg-on-surface/10"
          aria-label={t('rightSidebar.manager.openLabel')}
          aria-expanded={isManagerOpen}
        >
          <RiSettings4Line />
        </button>
        <WidgetManager
          isOpen={isManagerOpen}
          onClose={() => setIsManagerOpen(false)}
          preferences={managerPreferences}
          widgets={widgetRegistry}
          onToggle={toggleWidget}
          onMove={moveWidget}
          onReset={resetPreferences}
        />
      </div>
      {visibleWidgets.length === 0 && (
        <p className="px-2 text-sm text-on-surface/60">
          {t('rightSidebar.empty')}
        </p>
      )}
      {visibleWidgets.map(({ pref, widget }) => {
        const WidgetComponent = widget.component;
        return <WidgetComponent key={pref.id} />;
      })}
    </aside>
  );
};

RightSidebar.propTypes = {
  className: PropTypes.string,
  excludeWidgets: PropTypes.arrayOf(PropTypes.string),
};

export default RightSidebar;
